import {
  createSlice,
  createAsyncThunk,
  createListenerMiddleware,
  isAnyOf,
} from '@reduxjs/toolkit';
import { instance } from '@app/api/axios';
import { defaultUsersParams } from '@utils/tables/params';
import { showToaster } from '@shared/ui/messages/useToaster';
import i18n from '@app/i18n';

export const fetchMyUsers = createAsyncThunk(
  'my_users/fetchMyUsers',
  async (_, { rejectWithValue, getState }) => {
    const { params, search } = getState().my_users;

    const merged = { ...defaultUsersParams, ...params };
    const query = {
      page: merged.page,
      per_page: merged.per_page,
      sort_by: merged.sort_by,
      sort_dir: merged.sort_dir,
      ...(search && { search }),
    };

    try {
      const res = await instance.get('/my_users', { params: query });
      return res.data;
    } catch (err) {
      showToaster(
        'error',
        err?.response?.data?.message || i18n.t('request_error'),
      );
      return rejectWithValue(err.response?.data);
    }
  },
);

const myUsersSlice = createSlice({
  name: 'my_users',
  initialState: {
    users: [],
    count_total: 0,
    count_filtered: 0,
    params: defaultUsersParams,
    search: '',
    isInitialized: false,
    isLoading: true,
  },
  reducers: {
    setMyUsersParams: (state, action) => {
      state.params = { ...state.params, ...action.payload };
    },
    setMyUsersSearch: (state, action) => {
      state.search = action.payload;
      state.params = { ...state.params, page: 1 };
      if (state.users?.length === 0) {
        state.isInitialized = false;
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchMyUsers.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(fetchMyUsers.fulfilled, (state, action) => {
      state.isLoading = false;
      state.isInitialized = true;
      state.users = action.payload?.data;
      state.count_total = action.payload?.count_total;
      state.count_filtered = action.payload?.count_filtered;
    });
    builder.addCase(fetchMyUsers.rejected, (state) => {
      state.isLoading = false;
    });
  },
});

export const { setMyUsersParams, setMyUsersSearch } = myUsersSlice.actions;

export default myUsersSlice.reducer;

export const myUsersListener = createListenerMiddleware();

myUsersListener.startListening({
  matcher: isAnyOf(setMyUsersParams, setMyUsersSearch),
  effect: async (_, listenerApi) => {
    await listenerApi.dispatch(fetchMyUsers());
  },
});
